/**
 * Dashboard - V2
 * Clean, focused dashboard aligned with product requirements
 * Reduces card count from 8 to 5, improves clarity and user journey visibility
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useProfile } from '../contexts/ProfileContext';
import dashboardService from '../services/dashboardService';
import { authFetch } from '../utils/auth';
import { getFirstNameFromClaims } from '../utils/claims';

// Dashboard V2 components
import {
  GreetingSection,
  YourSupportJourneyCard,
  ContinueWhereLeftOffCard,
  AICoachingCard,
  WeeklyTipsCard,
  FamilyCard
} from '../components/dashboard-v2';

// Onboarding utilities
import { getFirstIncompleteStep } from '../utils/onboardingNext';

// Common components
import Header from '../components/common/Header';

// Wizard draft status to progress mapping
const WIZARD_STATUS_PROGRESS = {
  in_progress: 2,
  summary_reviewed: 2,
  generated: 3,
  downloaded: 4
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const { profile, loaded: profileLoaded } = useProfile();

  // Core data state
  const [activities, setActivities] = useState([]);
  const [latestDraft, setLatestDraft] = useState(null);
  const [journeyState, setJourneyState] = useState({
    stage: 'new',
    progress: 0,
    nextAction: null
  });

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch all dashboard data
   */
  const fetchDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch critical data in parallel:
      // 1. Activities feed (legacy drafts, conversations)
      // 2. New wizard drafts from /api/soknad/hjelpestonad/drafts
      const [activitiesData, wizardDraftsResponse] = await Promise.all([
        dashboardService.fetchRecentActivity(),
        authFetch('/soknad/hjelpestonad/drafts?limit=1')
          .then(res => res.json())
          .catch(() => [])
      ]);

      setActivities(activitiesData);

      // Track latest wizard draft for journey card
      const drafts = wizardDraftsResponse || [];
      setLatestDraft(drafts[0] || null);

      // Compute journey state from both data sources
      const computedJourneyState = computeJourneyState(activitiesData, drafts);
      setJourneyState(computedJourneyState);

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Kunne ikke laste dashboard. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Compute journey state from activities and wizard drafts
   */
  const computeJourneyState = (activities, wizardDraftsData = []) => {
    // Find legacy drafts and documents (exclude legacy drafts without conversationId)
    const legacyDrafts = activities.filter(a => a.type === 'draft' && a.metadata?.conversationId);
    const documents = activities.filter(a => a.type === 'document');

    // Check for wizard drafts (new system)
    const latestWizardDraft = wizardDraftsData[0] || null;

    let stage = 'new';
    let progress = 0;
    let nextAction = null;

    // Determine stage and progress
    // Only count "situation mapped" if profile is complete AND onboarding was NOT skipped
    if (profileLoaded && profile.profileComplete && !profile.onboardingSkipped) {
      progress = 1; // Situation mapped
      stage = 'situation_mapped';
    }

    // Check wizard draft status first (new system takes precedence)
    if (latestWizardDraft) {
      const wizardProgress = WIZARD_STATUS_PROGRESS[latestWizardDraft.status] || 2;
      if (wizardProgress > progress) {
        progress = wizardProgress;
        stage = progress >= 3 ? 'application_ready' : 'started_application';
      }
    }

    // Legacy drafts/documents (fallback for old data)
    if (legacyDrafts.length > 0 || documents.length > 0) {
      if (progress < 2) {
        progress = 2; // Started application
        stage = 'started_application';
      }
    }

    if (documents.length > 0) {
      if (progress < 3) {
        progress = 3; // Application ready
        stage = 'application_ready';
      }
    }

    // Determine next action (profile completion is handled by the separate banner)
    if (latestWizardDraft?.status === 'generated' || latestWizardDraft?.status === 'downloaded') {
      // Wizard draft ready for download
      nextAction = {
        type: 'submit_application',
        label: 'Se din søknad',
        description: 'Søknaden din er klar. Se over og last ned for å sende til Nav',
        action: () => navigate(`/soknad/hjelpestonad/${latestWizardDraft._id}/preview`)
      };
    } else if (latestWizardDraft?.status === 'summary_reviewed') {
      // Wizard draft ready for generation
      nextAction = {
        type: 'generate_document',
        label: 'Generer søknad',
        description: 'Søknaden er fullført. Generer dokumentet for innsending',
        action: () => navigate(`/soknad/hjelpestonad/${latestWizardDraft._id}/preview`)
      };
    } else if (latestWizardDraft?.status === 'in_progress') {
      // Continue wizard draft
      nextAction = {
        type: 'continue_draft',
        label: 'Fortsett søknad',
        description: 'Du har en påstartet søknad. Fortsett der du slapp',
        action: () => navigate(`/soknad/hjelpestonad/${latestWizardDraft._id}`)
      };
    } else if (documents.length > 0) {
      // Legacy: document ready. Its /applications preview page no longer
      // exists, so the conversation it came from is the only place to open.
      nextAction = {
        type: 'submit_application',
        label: 'Last ned og send inn',
        description: 'Soknaden din er klar. Last ned og send den til Nav',
        action: () => {
          const { conversationId } = documents[0].metadata;
          if (conversationId) {
            navigate(`/conversations/${conversationId}`);
          }
        }
      };
    } else if (legacyDrafts.length > 0) {
      // Legacy: continue old draft (legacyDrafts only holds drafts with a
      // conversationId — see the filter above)
      const latestDraft = legacyDrafts[0];
      const sectionsComplete = latestDraft.metadata?.sectionsComplete || 0;

      if (sectionsComplete === 100) {
        nextAction = {
          type: 'generate_document',
          label: 'Generer dokument',
          description: 'Søknaden er fullført. Generer dokumentet for innsending',
          action: () => navigate(`/conversations/${latestDraft.metadata.conversationId}`)
        };
      } else {
        nextAction = {
          type: 'continue_draft',
          label: 'Fortsett søknad',
          description: `Søknaden din er ${sectionsComplete}% fullført. La oss fullføre den sammen`,
          action: () => navigate(`/conversations/${latestDraft.metadata.conversationId}`)
        };
      }
    } else {
      // No draft exists - start new application
      nextAction = {
        type: 'start_application',
        label: 'Start hjelpestønad-søknad',
        description: 'Begynn på søknaden om hjelpestønad',
        action: () => handleStartApplication()
      };
    }

    return { stage, progress, nextAction };
  };

  /**
   * Handle activity click (draft, conversation, document)
   */
  const handleActivityClick = (activity) => {
    const { type, metadata } = activity;

    switch (type) {
      case 'draft':
        // Wizard (SoknadDraft) entries carry soknadDraftId + soknadType and
        // open that draft.
        if (metadata.soknadDraftId && metadata.soknadType) {
          navigate(`/soknad/${metadata.soknadType}/${metadata.soknadDraftId}`);
        } else if (metadata.conversationId) {
          // Legacy conversational FSM flow
          navigate(`/conversations/${metadata.conversationId}`);
        }
        // A legacy form-builder draft has nothing to open: its /applications
        // page no longer exists.
        break;

      case 'document':
        // Generated/downloaded wizard draft → that draft's preview page.
        if (metadata.soknadDraftId && metadata.soknadType) {
          navigate(`/soknad/${metadata.soknadType}/${metadata.soknadDraftId}/preview`);
        } else if (metadata.conversationId) {
          // Legacy document: its /applications preview page no longer exists,
          // so open the conversation it came from.
          navigate(`/conversations/${metadata.conversationId}`);
        }
        break;

      case 'conversation':
        navigate(`/conversations/${metadata.conversationId}`);
        break;

      default:
        console.warn('Unknown activity type:', type);
    }
  };

  /**
   * Handle new action (start conversation, start application)
   */
  const handleNewAction = async (actionType) => {
    switch (actionType) {
      case 'new_conversation':
        navigate('/new-conversation'); // Fixed: correct route is /new-conversation not /conversations/new
        break;

      case 'start_application':
        await handleStartApplication();
        break;

      default:
        console.warn('Unknown action type:', actionType);
    }
  };

  /**
   * Handle start new hjelpestonad application
   * Navigates to the wizard-based soknad flow
   */
  const handleStartApplication = () => {
    navigate('/soknad/hjelpestonad');
  };

  /**
   * Handle coaching topic click
   */
  const handleTopicClick = async (topic) => {
    try {
      const initialMessage = topic.context?.initialMessage || '';

      // Create conversation with initial message
      const response = await authFetch('/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: topic.label,
          initialMessage: initialMessage,
          metadata: {
            topic: topic.id,
            source: 'dashboard_coaching'
          }
        })
      });

      const data = await response.json();
      const conversationId = data._id;

      // Pass initialMessage in navigation state to trigger AI response
      navigate(`/conversations/${conversationId}`, {
        state: { initialMessage: initialMessage }
      });
    } catch (error) {
      console.error('Error starting conversation:', error);
      alert('Kunne ikke starte samtale. Prøv igjen.');
    }
  };

  /**
   * Handle journey action click
   */
  const handleJourneyActionClick = (action) => {
    if (action.action) {
      action.action();
    }
  };

  // Fetch data on mount and when navigating back to dashboard
  useEffect(() => {
    if (profileLoaded) {
      // Clear cache to ensure fresh data on navigation
      dashboardService.clearCache();
      fetchDashboardData();
    }
  }, [profileLoaded, location.key, fetchDashboardData]);

  // Show loading state
  if (!profileLoaded || isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-20 bg-gray-200 rounded-lg"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
            <div className="h-48 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-800">{error}</p>
            <button
              onClick={fetchDashboardData}
              className="mt-2 text-sm text-error-600 underline hover:text-error-700"
            >
              Prøv igjen
            </button>
          </div>
        </div>
      </div>
    );
  }

  const childName = profile?.child?.alias || 'barnet ditt';

  // First name from the Vipps-sourced ID token claims (BankID-verified; only
  // present if the user consented to sharing it). Null → nameless greeting.
  const userFirstName = getFirstNameFromClaims(
    instance.getActiveAccount() || accounts[0]
  );

  // Check if onboarding is incomplete and compute resume route.
  // Note: skipping (intro or mid-wizard) sets profileComplete=true to bypass the
  // redirect gate, so we also need to check onboardingSkipped explicitly.
  const needsOnboarding = profile?.onboardingSkipped || (!profile?.profileComplete && !profile?.wizardSkipped);
  const onboardingResumeStep = needsOnboarding ? getFirstIncompleteStep(profile) : null;

  // Determine if user has indicated hjelpestønad relevance:
  // Profile has child diagnoses/challenges, or needs assessment booleans set
  const hasHjelpestonadInterest = !!(
    latestDraft ||
    profile?.needsIntensiveSupport ||
    profile?.needsNightWatch ||
    profile?.needsContinuousSupervision ||
    (profile?.child?.diagnoses && profile.child.diagnoses.length > 0) ||
    (profile?.challenges && profile.challenges.length > 0)
  );

  return (
    <div className="min-h-screen bg-background">
      <Header variant="dashboard" size="sm">
        <GreetingSection
          userFirstName={userFirstName}
          childName={childName}
          journeyStage={journeyState.stage}
          profileComplete={profile?.profileComplete}
        />
      </Header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Resume onboarding banner */}
        {needsOnboarding && onboardingResumeStep && onboardingResumeStep !== '/home' && (
          <div className="mb-6 bg-[#FCFCF9] border border-border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-text-primary">
                {profile?.onboardingSkipped ? 'Fullfør profilen din' : 'Fullfør registreringen'}
              </p>
              <p className="text-sm text-text-secondary">
                {profile?.onboardingSkipped
                  ? 'Du hoppet over profilutfyllingen. Fullfør den for personlig tilpasset hjelp.'
                  : 'Fullfør profilen din slik at vi kan gi deg personlig veiledning.'}
              </p>
            </div>
            <button
              onClick={() => navigate(onboardingResumeStep)}
              className="flex-shrink-0 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors min-h-[48px]"
            >
              {profile?.onboardingSkipped ? 'Fullfør profilen' : 'Fullfør registrering'}
            </button>
          </div>
        )}

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column (2/3 width) */}
          <div className="lg:col-span-2 space-y-6">
            {/* 1. Your Support Journey Card */}
            <YourSupportJourneyCard
              journeyStage={journeyState.stage}
              progress={journeyState.progress}
              nextAction={journeyState.nextAction}
              onActionClick={handleJourneyActionClick}
              hasHjelpestonadInterest={hasHjelpestonadInterest}
              latestDraft={latestDraft}
            />

            {/* 2. Continue Where You Left Off Card */}
            <ContinueWhereLeftOffCard
              activities={activities}
              onActivityClick={handleActivityClick}
              onNewAction={handleNewAction}
            />
          </div>

          {/* Right Column (1/3 width) */}
          <div className="space-y-6">
            {/* 3. AI Coaching Card */}
            <AICoachingCard
              profile={profile}
              onTopicClick={handleTopicClick}
            />

            {/* 4. Weekly Tips Card */}
            <WeeklyTipsCard />

            {/* 5. Family Card */}
            <FamilyCard />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
